import type { HudState, ThrowOutcome } from '../game/types';

export type HudCallbacks = {
  onRestart: () => void;
  onToggleMute: () => boolean;
};

type StatusCopy = {
  label: string;
  line: string;
};

const TOTAL_BALLS = 10;

export class Hud {
  private readonly root: HTMLElement;
  private readonly ballRack: HTMLElement;
  private readonly ballPips: HTMLElement[];
  private readonly ballsNumber: HTMLElement;
  private readonly ticketScore: HTMLOutputElement;
  private readonly specialHits: HTMLElement;
  private readonly specialTotal: HTMLElement;
  private readonly statusBanner: HTMLElement;
  private readonly statusLabel: HTMLElement;
  private readonly statusLine: HTMLElement;
  private readonly powerDock: HTMLElement;
  private readonly powerMeter: HTMLElement;
  private readonly powerValue: HTMLOutputElement;
  private readonly soundButton: HTMLButtonElement;
  private readonly restartButton: HTMLButtonElement;
  private readonly replayButton: HTMLButtonElement;
  private readonly roundOverlay: HTMLElement;
  private readonly roundMessage: HTMLElement;
  private readonly finalScore: HTMLOutputElement;
  private readonly finalSpecialHits: HTMLElement;
  private readonly finalSpecialTotal: HTMLElement;

  private previousBalls = TOTAL_BALLS;
  private previousPhase: HudState['phase'] = 'ready';
  private lastOutcomeKey = '';
  private focusFrame: number | null = null;
  private disposed = false;

  constructor(private readonly callbacks: HudCallbacks) {
    this.root = this.getElement<HTMLElement>('#hud');
    this.ballRack = this.getElement<HTMLElement>('#ball-pips');
    this.ballPips = Array.from(this.ballRack.querySelectorAll<HTMLElement>('.ball-pip'));
    this.ballsNumber = this.getElement<HTMLElement>('#balls-number');
    this.ticketScore = this.getElement<HTMLOutputElement>('#ticket-score');
    this.specialHits = this.getElement<HTMLElement>('#special-hits');
    this.specialTotal = this.getElement<HTMLElement>('#special-total');
    this.statusBanner = this.getElement<HTMLElement>('#status-banner');
    this.statusLabel = this.getElement<HTMLElement>('#status-label');
    this.statusLine = this.getElement<HTMLElement>('#status-line');
    this.powerDock = this.getElement<HTMLElement>('#power-dock');
    this.powerMeter = this.getElement<HTMLElement>('#power-meter');
    this.powerValue = this.getElement<HTMLOutputElement>('#power-value');
    this.soundButton = this.getElement<HTMLButtonElement>('#sound-button');
    this.restartButton = this.getElement<HTMLButtonElement>('#restart-button');
    this.replayButton = this.getElement<HTMLButtonElement>('#replay-button');
    this.roundOverlay = this.getElement<HTMLElement>('#round-over');
    this.roundMessage = this.getElement<HTMLElement>('#round-message');
    this.finalScore = this.getElement<HTMLOutputElement>('#final-score');
    this.finalSpecialHits = this.getElement<HTMLElement>('#final-special-hits');
    this.finalSpecialTotal = this.getElement<HTMLElement>('#final-special-total');

    if (this.ballPips.length !== TOTAL_BALLS) {
      throw new Error(`Expected ${TOTAL_BALLS} ball pips, found ${this.ballPips.length}.`);
    }

    this.soundButton.addEventListener('click', this.handleMute);
    this.restartButton.addEventListener('click', this.handleRestart);
    this.replayButton.addEventListener('click', this.handleRestart);

    for (const button of [this.soundButton, this.restartButton, this.replayButton]) {
      button.addEventListener('pointerdown', this.stopGamePointerInput);
    }
  }

  update(state: HudState): void {
    if (this.disposed) return;

    this.root.dataset.phase = state.phase;
    this.updateBallRack(state.ballsRemaining);

    const scoreText = this.formatScore(state.score);
    this.ticketScore.textContent = scoreText;
    this.specialHits.textContent = String(Math.max(0, Math.floor(state.specialHits)));
    this.specialTotal.textContent = String(Math.max(0, Math.floor(state.totalSpecial)));
    this.setMuted(state.muted);

    const defaultCopy = this.getStatusCopy(state);
    this.statusLabel.textContent = defaultCopy.label;
    this.statusLine.textContent = defaultCopy.line;

    const outcomeIsCurrent =
      state.lastOutcome !== null &&
      (state.phase === 'resolving' || state.phase === 'ready' || state.phase === 'round-over');

    if (outcomeIsCurrent && state.lastOutcome) {
      const outcomeKey = this.getOutcomeKey(state.lastOutcome);
      const shouldAnimate = outcomeKey !== this.lastOutcomeKey;
      this.applyOutcomeCopy(state.lastOutcome, shouldAnimate);
    } else {
      delete this.statusBanner.dataset.outcome;
      if (state.phase === 'charging' || state.phase === 'in-flight') this.lastOutcomeKey = '';
    }

    this.setVisiblePower(
      state.power,
      state.phase === 'ready' || state.phase === 'charging',
    );

    const roundOver = state.phase === 'round-over';
    this.updateRoundOverlay(state, scoreText, roundOver);
    this.previousPhase = state.phase;
  }

  announceOutcome(outcome: ThrowOutcome): void {
    if (this.disposed) return;
    this.applyOutcomeCopy(outcome, true);
  }

  setVisiblePower(power: number, visible = true): void {
    if (this.disposed) return;
    const normalized = this.normalizePower(power);
    const percent = Math.round(normalized * 100);

    this.powerDock.style.setProperty('--power', `${percent}%`);
    this.powerDock.dataset.visible = String(visible);
    this.powerValue.textContent = `${percent}%`;
    this.powerMeter.setAttribute('aria-valuenow', String(percent));
    this.powerMeter.setAttribute('aria-valuetext', `${percent} percent`);
  }

  setMuted(muted: boolean): void {
    if (this.disposed) return;
    this.soundButton.setAttribute('aria-pressed', String(muted));
    this.soundButton.setAttribute('aria-label', muted ? 'Turn sound on' : 'Turn sound off');
    this.soundButton.title = muted ? 'Sound off' : 'Sound on';
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.soundButton.removeEventListener('click', this.handleMute);
    this.restartButton.removeEventListener('click', this.handleRestart);
    this.replayButton.removeEventListener('click', this.handleRestart);

    for (const button of [this.soundButton, this.restartButton, this.replayButton]) {
      button.removeEventListener('pointerdown', this.stopGamePointerInput);
    }

    if (this.focusFrame !== null) cancelAnimationFrame(this.focusFrame);
    this.focusFrame = null;
  }

  private readonly handleRestart = (event: Event): void => {
    event.stopPropagation();
    this.roundOverlay.hidden = true;
    this.callbacks.onRestart();
    document.querySelector<HTMLCanvasElement>('#game-canvas')?.focus({ preventScroll: true });
  };

  private readonly handleMute = (event: Event): void => {
    event.stopPropagation();
    const muted = this.callbacks.onToggleMute();
    this.setMuted(muted);
  };

  private readonly stopGamePointerInput = (event: PointerEvent): void => {
    event.stopPropagation();
  };

  private updateBallRack(ballsRemaining: number): void {
    const balls = Math.min(TOTAL_BALLS, Math.max(0, Math.floor(ballsRemaining)));
    this.ballRack.setAttribute(
      'aria-label',
      `${balls} of ${TOTAL_BALLS} ${balls === 1 ? 'ball' : 'balls'} remaining`,
    );
    this.ballsNumber.textContent = String(balls);

    this.ballPips.forEach((pip, index) => {
      const spent = index >= balls;
      const newlySpent = spent && index < this.previousBalls && index >= balls;
      pip.classList.toggle('is-spent', spent);

      if (newlySpent) {
        pip.classList.remove('just-spent');
        void pip.offsetWidth;
        pip.classList.add('just-spent');
        pip.addEventListener('animationend', () => pip.classList.remove('just-spent'), {
          once: true,
        });
      } else if (!spent) {
        pip.classList.remove('just-spent');
      }
    });

    this.previousBalls = balls;
  }

  private updateRoundOverlay(state: HudState, scoreText: string, roundOver: boolean): void {
    this.roundOverlay.hidden = !roundOver;
    if (!roundOver) return;

    this.finalScore.textContent = scoreText;
    this.finalSpecialHits.textContent = String(Math.max(0, Math.floor(state.specialHits)));
    this.finalSpecialTotal.textContent = String(Math.max(0, Math.floor(state.totalSpecial)));
    this.roundMessage.textContent = this.getRoundMessage(state.specialHits, state.totalSpecial);

    if (this.previousPhase !== 'round-over') {
      if (this.focusFrame !== null) cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = null;
        if (!this.disposed && !this.roundOverlay.hidden) {
          this.replayButton.focus({ preventScroll: true });
        }
      });
    }
  }

  private getStatusCopy(state: HudState): StatusCopy {
    const requestedStatus = state.status.trim();

    switch (state.phase) {
      case 'ready':
        return { label: 'Ready', line: requestedStatus || 'Pick a prize cup' };
      case 'charging':
        return { label: 'Charging', line: requestedStatus || 'Release to toss' };
      case 'in-flight':
        return { label: 'In flight', line: requestedStatus || 'Watch the bounce' };
      case 'resolving':
        return { label: 'Judging', line: requestedStatus || 'Let it settle…' };
      case 'round-over':
        return { label: 'Final', line: requestedStatus || 'Round complete' };
    }
  }

  private applyOutcomeCopy(outcome: ThrowOutcome, animate: boolean): void {
    const copy = this.getOutcomeCopy(outcome);
    this.statusBanner.dataset.outcome = outcome.kind;
    this.statusLabel.textContent = copy.label;
    this.statusLine.textContent = copy.line;
    this.lastOutcomeKey = this.getOutcomeKey(outcome);

    if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.statusBanner.classList.remove('is-popping');
    void this.statusBanner.offsetWidth;
    this.statusBanner.classList.add('is-popping');
    this.statusBanner.addEventListener(
      'animationend',
      () => this.statusBanner.classList.remove('is-popping'),
      { once: true },
    );
  }

  private getOutcomeCopy(outcome: ThrowOutcome): StatusCopy {
    switch (outcome.kind) {
      case 'special': {
        const reward = outcome.scoreAwarded > 0 ? ` +${outcome.scoreAwarded} tickets` : '';
        return { label: 'Prize cup!', line: `Jackpot${reward}` };
      }
      case 'normal':
        return { label: 'In the cup', line: 'Nice landing — prize cups score' };
      case 'miss':
        return { label: 'So close', line: 'Line up the next toss' };
    }
  }

  private getRoundMessage(specialHits: number, totalSpecial: number): string {
    if (specialHits <= 0) return 'The cups win this one. Line up another toss!';
    if (totalSpecial > 0 && specialHits >= totalSpecial) return 'Every prize cup cleared — carnival legend!';
    if (specialHits === 1) return 'One prize cup! The booth is buzzing.';
    if (specialHits >= 3) return 'A prize-winning cascade. What a round!';
    return 'Great shooting — those prize cups never stood a chance.';
  }

  private getOutcomeKey(outcome: ThrowOutcome): string {
    return `${outcome.kind}:${outcome.cup?.index ?? -1}:${outcome.scoreAwarded}`;
  }

  private normalizePower(power: number): number {
    if (!Number.isFinite(power)) return 0;
    const normalized = power > 1 ? power / 100 : power;
    return Math.min(1, Math.max(0, normalized));
  }

  private formatScore(score: number): string {
    const safeScore = Number.isFinite(score) ? Math.max(0, Math.round(score)) : 0;
    return String(safeScore).padStart(4, '0');
  }

  private getElement<T extends HTMLElement>(selector: string): T {
    const element = document.querySelector<T>(selector);
    if (!element) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}
